import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { CustomerSelect } from '@/components/CustomerSelect';
import { FormGroup } from '@/components/FormGroup';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useCreateVoiceToneExport } from '@/hooks/useCreateVoiceToneExport';
import { queryClient } from '@/lib/queryClient';
import { PromptSelect } from '@/pages/admin/Models/components/PromptSelect';
import { Prompt } from '@/services/PromptsService';

const schema = z.object({
  prompt: z.string().min(1, 'Prompt is required'),
  customer: z.string().min(1, 'Customer is required'),
});

type FormData = z.infer<typeof schema>;

export function ExportForm() {
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      prompt: '',
      customer: '',
    },
  });

  const { createVoiceToneExport, isLoading } = useCreateVoiceToneExport();

  const onSubmit = handleSubmit(async (data: FormData) => {
    if (!selectedPrompt) {
      toast.error('Please select a prompt template');
      return;
    }

    try {
      await createVoiceToneExport({
        promptName: selectedPrompt.promptName,
        version: selectedPrompt.version,
        customerId: parseInt(data.customer, 10),
      });
      toast.success('Export created successfully');
      reset();
      setSelectedPrompt(null);
      queryClient.invalidateQueries({ queryKey: ['voice-tone-exports'] });
    } catch (err) {
      toast.error('Failed to create export');
    }
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create New Export</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-6">
            <FormGroup
              error={{ message: errors.customer?.message, font: 'sm' }}
            >
              <RequiredLabel htmlFor="customer">Customer</RequiredLabel>
              <Controller
                name="customer"
                control={control}
                render={({ field }) => (
                  <CustomerSelect
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  />
                )}
              />
            </FormGroup>

            <FormGroup error={{ message: errors.prompt?.message, font: 'sm' }}>
              <RequiredLabel htmlFor="prompt">Prompt Template</RequiredLabel>
              <Controller
                name="prompt"
                control={control}
                render={({ field }) => (
                  <PromptSelect
                    value={field.value}
                    onChange={(val, prompt) => {
                      field.onChange(String(val));
                      setSelectedPrompt(prompt || null);
                    }}
                  />
                )}
              />
            </FormGroup>
          </div>
          <div className="flex w-full justify-end">
            <Button type="submit" disabled={isSubmitting || isLoading}>
              {isSubmitting || isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Create Export
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
